import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import api, {
  API_ERRORS,
  getApiErrorMessage,
  getApiErrorName,
} from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { ArrowBackIcon } from '../../../assets/svg/SVGComponents';
import { categoryLabel } from '../../../utils/tournament/labels';
import { formatWallClockTime } from '../../../utils/scheduleDate';
import useNow from '../../TournamentBoard/hooks/useNow';
import {
  EMPTY_SCORES,
  PresentationForm,
  PresentationTimer,
  totalScore,
  TPresentationScores,
  usePresentationDrafts,
} from '../../../components/Tournament/PresentationScoring';
import {
  agendaMarks,
  IAgendaSchedule,
  pickInitialTeam,
  sortByAgenda,
  wallClockNow,
} from '../../../utils/tournament/presentationAgenda';
import {
  BackLink,
  Chips,
  Empty,
  GroupLabel,
  Header,
  Layout,
  Mark,
  Page,
  Progress,
  Status,
  TeamItem,
  TeamList,
} from './styles';

interface IPresentationItem {
  team_id: string;
  team_name: string;
  school_name: string | null;
  category: string;
  disqualified: boolean;
  status: 'scored' | 'pending';
  scores: (TPresentationScores & { total_score: number }) | null;
  /** Horário na agenda da sala (BE-49); null sem horário. */
  schedule?: IAgendaSchedule | null;
}

const ROOM_KEY = (id: string) => `presentations-room:${id}`;

const readRoom = (id: string): string | null => {
  try {
    return window.localStorage.getItem(ROOM_KEY(id));
  } catch {
    return null;
  }
};

const writeRoom = (id: string, room: string | null) => {
  try {
    if (room) window.localStorage.setItem(ROOM_KEY(id), room);
    else window.localStorage.removeItem(ROOM_KEY(id));
  } catch {
    // Sem armazenamento (aba anônima): o filtro só não é lembrado.
  }
};

const pickScores = (item: IPresentationItem): TPresentationScores =>
  item.scores
    ? {
        mechanical_score: item.scores.mechanical_score,
        programming_score: item.scores.programming_score,
        teamwork_score: item.scores.teamwork_score,
        communication_score: item.scores.communication_score,
      }
    : { ...EMPTY_SCORES };

/**
 * Banca da Apresentação Técnica (etapa nacional, categorias 3 e 4) — tela
 * antiga, mantida por enquanto (FE-68). A avaliação principal é na aba
 * Apresentações do torneio; as duas usam o mesmo rascunho e o mesmo
 * cronômetro (`usePresentationDrafts`), então mostram as mesmas notas.
 */
const TournamentPresentations: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [tournamentName, setTournamentName] = useState('');
  const [items, setItems] = useState<IPresentationItem[]>([]);
  const drafts = usePresentationDrafts(id);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [room, setRoomState] = useState<string | null>(() => readRoom(id));
  // A banca fica com a tela aberta: "agora" e "a seguir" andam com o relógio.
  const nowWall = wallClockNow(useNow(30_000));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const leave = useCallback(
    (description: string) => {
      addToast({ type: 'error', expression: 'Ops!', description });
      navigate(`/tournament/${id}`, { replace: true });
    },
    [addToast, id, navigate],
  );

  useEffect(() => {
    let alive = true;

    Promise.all([
      api.get<{ name: string }>(`/tournaments/${id}`),
      api.get<IPresentationItem[]>(
        `/tournaments/${id}/technical-presentations`,
      ),
    ])
      .then(([{ data: tournament }, { data: list }]) => {
        if (!alive) return;
        setTournamentName(tournament.name);
        setItems(list);
        setSelectedId(pickInitialTeam(list, wallClockNow()));
      })
      .catch(err => {
        if (!alive) return;
        leave(
          getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE
            ? 'Só os juízes cadastrados no torneio acessam a banca da apresentação técnica.'
            : (getApiErrorMessage(err) ??
                'Não foi possível carregar a banca da apresentação técnica.'),
        );
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [id, leave]);

  const categories = useMemo(
    () => Array.from(new Set(items.map(item => item.category))).sort(),
    [items],
  );

  const rooms = useMemo(() => {
    const byId = new Map<string, string>();
    items.forEach(item => {
      if (item.schedule)
        byId.set(item.schedule.room_id, item.schedule.room_name);
    });
    return Array.from(byId, ([value, name]) => ({ id: value, name }));
  }, [items]);

  // Sala lembrada que não existe mais (ou agenda com uma sala só) = todas.
  const activeRoom =
    rooms.some(r => r.id === room) && rooms.length > 1 ? room : null;

  const setRoom = (value: string | null) => {
    setRoomState(value);
    writeRoom(id, value);
  };

  const visible = useMemo(
    () =>
      sortByAgenda(
        items.filter(
          item =>
            (!category || item.category === category) &&
            (!activeRoom || item.schedule?.room_id === activeRoom),
        ),
      ),
    [items, category, activeRoom],
  );

  const marks = useMemo(() => agendaMarks(items, nowWall), [items, nowWall]);
  const scheduled = visible.filter(item => item.schedule);
  const unscheduled = visible.filter(item => !item.schedule);
  const showRoomName = rooms.length > 1;

  const scoredCount = items.filter(item => item.status === 'scored').length;
  const selected = items.find(item => item.team_id === selectedId) ?? null;

  const save = async () => {
    if (!selected) return;
    const scores = drafts.scoresOf(selected.team_id, pickScores(selected));
    setSaving(true);

    try {
      await api.post(
        `/tournaments/${id}/teams/${selected.team_id}/technical-presentation`,
        scores,
      );

      const updated = items.map(item =>
        item.team_id === selected.team_id
          ? {
              ...item,
              status: 'scored' as const,
              scores: { ...scores, total_score: totalScore(scores) },
            }
          : item,
      );
      setItems(updated);
      drafts.clearScores(selected.team_id);

      addToast({
        type: 'success',
        expression: 'Avaliação salva',
        description: `${selected.team_name}: ${totalScore(scores)} pontos.`,
      });

      // Segue para a próxima equipe pendente da agenda, na mesma lista.
      const next = pickInitialTeam(
        updated.filter(
          item =>
            item.status === 'pending' &&
            (!category || item.category === category) &&
            (!activeRoom || item.schedule?.room_id === activeRoom),
        ),
        wallClockNow(),
      );
      if (next) setSelectedId(next);
    } catch (err) {
      if (getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE) {
        leave(
          'Você não está cadastrado como juiz deste torneio. Peça à organização para incluir você.',
        );
        return;
      }
      addToast({
        type: 'error',
        expression: 'Não foi possível salvar',
        description: getApiErrorMessage(err) ?? 'Tente novamente em instantes.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <BackLink type="button" onClick={() => navigate(`/tournament/${id}`)}>
        <ArrowBackIcon
          aria-hidden
          width="1.33rem"
          stroke="#1E88E5"
          strokeWidth={3}
          fill="none"
        />
        {tournamentName || 'Torneio'}
      </BackLink>

      <Header>
        <div>
          <h1>Apresentação técnica</h1>
          <p>Banca da etapa nacional · categorias III e IV</p>
        </div>
        {!loading && items.length > 0 && (
          <Progress>
            {scoredCount} <span>de</span> {items.length} <span>avaliadas</span>
          </Progress>
        )}
      </Header>

      {loading && <Empty>Carregando equipes…</Empty>}

      {!loading && items.length === 0 && (
        <Empty>Nenhuma equipe com apresentação técnica neste torneio.</Empty>
      )}

      {!loading && items.length > 0 && (
        <Layout>
          <div>
            {rooms.length > 1 && (
              <Chips role="group" aria-label="Filtrar por sala">
                <button
                  type="button"
                  aria-pressed={activeRoom === null}
                  onClick={() => setRoom(null)}
                >
                  Todas as salas
                </button>
                {rooms.map(value => (
                  <button
                    key={value.id}
                    type="button"
                    aria-pressed={activeRoom === value.id}
                    onClick={() => setRoom(value.id)}
                  >
                    {value.name}
                  </button>
                ))}
              </Chips>
            )}

            {categories.length > 1 && (
              <Chips role="group" aria-label="Filtrar por categoria">
                <button
                  type="button"
                  aria-pressed={category === null}
                  onClick={() => setCategory(null)}
                >
                  Todas
                </button>
                {categories.map(value => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={category === value}
                    onClick={() => setCategory(value)}
                  >
                    {categoryLabel(value)}
                  </button>
                ))}
              </Chips>
            )}

            {[
              { key: 'scheduled', label: null, list: scheduled },
              {
                key: 'unscheduled',
                label: scheduled.length > 0 ? 'Sem horário' : null,
                list: unscheduled,
              },
            ]
              .filter(group => group.list.length > 0)
              .map(group => (
                <React.Fragment key={group.key}>
                  {group.label && <GroupLabel>{group.label}</GroupLabel>}
                  <TeamList>
                    {group.list.map(item => {
                      const mark = marks[item.team_id];
                      return (
                        <TeamItem
                          key={item.team_id}
                          $active={item.team_id === selectedId}
                        >
                          <button
                            type="button"
                            aria-current={item.team_id === selectedId}
                            onClick={() => setSelectedId(item.team_id)}
                          >
                            <strong>
                              {item.schedule && (
                                <time dateTime={item.schedule.starts_at}>
                                  {formatWallClockTime(item.schedule.starts_at)}
                                </time>
                              )}
                              {item.team_name}
                              {mark && (
                                <Mark $mark={mark}>
                                  {mark === 'now' ? 'Agora' : 'A seguir'}
                                </Mark>
                              )}
                            </strong>
                            <Status $scored={item.status === 'scored'}>
                              {item.status === 'scored'
                                ? `${item.scores?.total_score ?? 0} pts`
                                : 'Pendente'}
                            </Status>
                            <small>
                              {[
                                showRoomName ? item.schedule?.room_name : null,
                                item.school_name,
                                categoryLabel(item.category),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                              {item.disqualified ? ' · desclassificada' : ''}
                            </small>
                          </button>
                        </TeamItem>
                      );
                    })}
                  </TeamList>
                </React.Fragment>
              ))}

            {visible.length === 0 && (
              <Empty>Nenhuma equipe com estes filtros.</Empty>
            )}
          </div>

          {selected ? (
            <PresentationForm
              key={selected.team_id}
              teamName={selected.team_name}
              subtitle={[
                selected.schedule
                  ? `${formatWallClockTime(selected.schedule.starts_at)}–${formatWallClockTime(selected.schedule.ends_at)} · ${selected.schedule.room_name}`
                  : null,
                selected.school_name,
                categoryLabel(selected.category),
              ]
                .filter(Boolean)
                .join(' · ')}
              scores={drafts.scoresOf(selected.team_id, pickScores(selected))}
              alreadyScored={selected.status === 'scored'}
              saving={saving}
              onChange={scores => drafts.setScores(selected.team_id, scores)}
              onSave={save}
              timer={
                <PresentationTimer
                  timer={drafts.timerOf(selected.team_id)}
                  onStart={() => drafts.start(selected.team_id)}
                  onPause={() => drafts.pause(selected.team_id)}
                  onReset={() => drafts.reset(selected.team_id)}
                />
              }
            />
          ) : (
            <Empty>Escolha uma equipe na lista.</Empty>
          )}
        </Layout>
      )}
    </Page>
  );
};

export default TournamentPresentations;
